import React, { useContext, useEffect, useState } from 'react';
import {
  FaUsers,
  FaBuilding,
  FaBox,
  FaTicketAlt,
  FaMapMarkerAlt,
  FaHotel,
  FaMoneyBillWave,
  FaEye,
  FaFileExport,
  FaChartLine,
} from 'react-icons/fa';
import {
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import '../../../assets/css/admin/adminDashboard/dashboardhome.css';
import axios from 'axios';
import AppContext from '../../../context/AppContext';

const DashboardHome = () => {
  const { baseUrl, authToken } = useContext(AppContext);

  const [counts, setCounts] = useState({
    customers: 0,
    vendors: 0,
    hotels: 0,
    packages: 0,
    destinations: 0,
    destination_spots: 0,
  });
  const [bookings, setBookings] = useState({ packages: 0, hotels: 0 });
  const [revenue, setRevenue] = useState({ packages: 0, hotels: 0 });
  const [bookingStatusBreakdown, setBookingStatusBreakdown] = useState([]);
  const [revenueByMonth, setRevenueByMonth] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadDashboardStats = () => {
    setLoading(true);
    axios.get(`${baseUrl}/admin/dashboard-stats`, {
      headers: { "Authorization": `Bearer ${authToken}` }
    })
      .then(res => {
        setCounts(res.data.counts);
        setBookings(res.data.bookings);
        setRevenue(res.data.revenue);
        setBookingStatusBreakdown(res.data.booking_status_breakdown);
        setRevenueByMonth(res.data.revenue_by_month);
        setLoading(false);
      })
      .catch(err => {
        console.log(err);
        setLoading(false);
      });
  };

  useEffect(() => {
    loadDashboardStats(authToken);
  }, []);

  const statusColors = {
    pending: '#f59e0b',
    confirmed: '#10b981',
    completed: '#2563eb',
    cancelled: '#ef4444',
  };

  const bookingStatusChartData = bookingStatusBreakdown.map((item) => ({
    name: item.booking_status,
    value: item.total,
    color: statusColors[item.booking_status] || '#6b7280',
  }));

  const statCards = [
    { title: 'Total Customers', value: counts.customers, icon: FaUsers, bgGradient: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)' },
    { title: 'Total Vendors', value: counts.vendors, icon: FaBuilding, bgGradient: 'linear-gradient(135deg, #10b981 0%, #047857 100%)' },
    { title: 'Total Hotels', value: counts.hotels, icon: FaHotel, bgGradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' },
    { title: 'Total Packages', value: counts.packages, icon: FaBox, bgGradient: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)' },
    { title: 'Total Destinations', value: counts.destinations, subtitle: `${counts.destination_spots} spots`, icon: FaMapMarkerAlt, bgGradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' },
    { title: 'Package Bookings', value: bookings.packages, icon: FaTicketAlt, bgGradient: 'linear-gradient(135deg, #2C5F7C 0%, #1e4a6b 100%)' },
    { title: 'Hotel Bookings', value: bookings.hotels, icon: FaTicketAlt, bgGradient: 'linear-gradient(135deg, #4A9E8E 0%, #2f6d61 100%)' },
    { title: 'Revenue - Packages', value: `Rs ${Number(revenue.packages).toLocaleString()}`, icon: FaMoneyBillWave, bgGradient: 'linear-gradient(135deg, #FFB800 0%, #cc9300 100%)' },
    { title: 'Revenue - Hotels', value: `Rs ${Number(revenue.hotels).toLocaleString()}`, icon: FaMoneyBillWave, bgGradient: 'linear-gradient(135deg, #10b981 0%, #047857 100%)' },
  ];

  const getStatusBadge = (status) => {
    const color = statusColors[status] || '#6b7280';
    return (
      <span className="jj-status-badge" style={{ background: `${color}15`, color: color }}>
        {status}
      </span>
    );
  };

  if (loading) {
    return <div className="jj-dashboard-home">Loading dashboard...</div>;
  }

  return (
    <div className="jj-dashboard-home">

      {/* Stats Cards */}
      <div className="jj-stats-grid">
        {statCards.map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <div key={idx} className="jj-stat-card">
              <div className="jj-stat-icon" style={{ background: stat.bgGradient }}>
                <Icon />
              </div>
              <div className="jj-stat-info">
                <span className="jj-stat-label">{stat.title}</span>
                <h3 className="jj-stat-value">{stat.value}</h3>
                {stat.subtitle && <div className="jj-stat-subtitle">{stat.subtitle}</div>}
              </div>
            </div>
          );
        })}
      </div>

      <div className="jj-charts-row">
        <div className="jj-chart-card large">
          <div className="jj-card-header">
            <div className="jj-card-title">
              <FaChartLine /> Revenue (Last 6 Months)
            </div>
          </div>
          <div className="jj-card-body">
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={revenueByMonth}>
                <defs>
                  <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1e4a6b" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#1e4a6b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                <Area type="monotone" dataKey="revenue" stroke="#1e4a6b" strokeWidth={2} fill="url(#revenueGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="jj-chart-card">
          <div className="jj-card-header">
            <div className="jj-card-title">
              <FaTicketAlt /> Booking Status
            </div>
          </div>
          <div className="jj-card-body">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={bookingStatusChartData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value">
                  {bookingStatusChartData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="jj-legend">
              {bookingStatusChartData.map((item) => (
                <div key={item.name} className="jj-legend-item">
                  <span className="jj-legend-color" style={{ background: item.color }} />
                  <span className="jj-legend-name">{item.name}</span>
                  <span className="jj-legend-value">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      </div>
  );
};

export default DashboardHome;